import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconX, IconArrowsMaximize } from '@tabler/icons-react';
import { detailImageUrl, thumbImageUrl, fullImageUrl, CARD_BACKDROP } from '../Shop_Functions/cloudinary';

const SWIPE_MIN_PX = 50;

// Round icon button used in the full-screen viewer
const ViewerButton = ({ label, onClick, className, children }) => (
    <button
        type='button'
        aria-label={label}
        onClick={onClick}
        className={`flex items-center justify-center w-12 h-12 rounded-full border border-solid border-zinc-700 bg-black/60 text-white cursor-pointer hover:border-white ${className}`}
    >
        {children}
    </button>
);

// The main photo with thumbnails underneath, and a full-screen viewer
// (arrow keys and swipes move between photos; Escape closes)
const ItemGallery = ({ photos, title }) => {
    const [index, setIndex] = useState(0);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [loadedFullSrc, setLoadedFullSrc] = useState(null);
    const touchStartX = useRef(null);
    const count = photos.length;

    // Another piece (e.g. from "More bowls") starts at its first photo
    useEffect(() => {
        setIndex(0);
        setViewerOpen(false);
    }, [photos]);

    const showPrevious = () => setIndex((i) => (i - 1 + count) % count);
    const showNext = () => setIndex((i) => (i + 1) % count);

    useEffect(() => {
        if (!viewerOpen || count < 2) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'ArrowRight') setIndex((i) => (i + 1) % count);
            if (event.key === 'ArrowLeft') setIndex((i) => (i - 1 + count) % count);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [viewerOpen, count]);

    const onTouchStart = (event) => { touchStartX.current = event.touches[0].clientX; };
    const onTouchEnd = (event) => {
        if (touchStartX.current === null || count < 2) return;
        const dx = event.changedTouches[0].clientX - touchStartX.current;
        if (dx <= -SWIPE_MIN_PX) showNext();
        if (dx >= SWIPE_MIN_PX) showPrevious();
        touchStartX.current = null;
    };

    return (
        <div className='flex flex-col gap-3'>
            <button
                type='button'
                onClick={() => setViewerOpen(true)}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                aria-label={`View ${title} full screen${count > 1 ? `, photo ${index + 1} of ${count}` : ''}`}
                className='group relative block w-full aspect-[4/3] max-h-[70vh] p-0 border-0 rounded-lg overflow-hidden cursor-zoom-in'
                style={{ backgroundColor: `#${CARD_BACKDROP}` }}
            >
                {/* One frame for every photo, so the page doesn't jump between them; the whole photo fits inside */}
                <img
                    src={detailImageUrl(photos[index])}
                    alt={title}
                    className='absolute inset-0 w-full h-full object-contain'
                />
                <span className='absolute bottom-3 right-3 flex items-center justify-center w-9 h-9 rounded-full bg-black/60 text-white opacity-70 transition-opacity group-hover:opacity-100'>
                    <IconArrowsMaximize size={18} aria-hidden='true' />
                </span>
            </button>

            {count > 1 &&
                <div className='flex flex-row gap-2' role='group' aria-label='Photos'>
                    {photos.map((photo, i) => (
                        <button
                            key={photo}
                            type='button'
                            onClick={() => setIndex(i)}
                            aria-label={`Show photo ${i + 1} of ${count}`}
                            aria-current={i === index}
                            className={`w-16 h-16 p-0 rounded-md overflow-hidden cursor-pointer border-2 border-solid transition-colors ${i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}
                        >
                            <img src={thumbImageUrl(photo)} alt='' loading='lazy' className='w-full h-full object-contain' />
                        </button>
                    ))}
                </div>
            }

            <Modal
                opened={viewerOpen}
                onClose={() => setViewerOpen(false)}
                fullScreen
                withCloseButton={false}
                title={`${title}${count > 1 ? `, photo ${index + 1} of ${count}` : ''}`}
                styles={{
                    content: { backgroundColor: '#000' },
                    // Keep the title for screen readers without showing a header bar
                    header: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', padding: 0 },
                    body: { padding: 0, height: '100%' },
                }}
            >
                <div
                    className='relative flex items-center justify-center w-full h-full'
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {/* The page's 1200px photo shows at once; the 2000px one fades in over it when loaded */}
                    <div className='relative w-[92vw] h-[85vh]'>
                        <img
                            src={detailImageUrl(photos[index])}
                            alt=''
                            className='absolute inset-0 w-full h-full object-contain'
                        />
                        <img
                            key={fullImageUrl(photos[index])}
                            src={fullImageUrl(photos[index])}
                            alt={title}
                            onLoad={(event) => setLoadedFullSrc(event.currentTarget.src)}
                            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${loadedFullSrc === fullImageUrl(photos[index]) ? 'opacity-100' : 'opacity-0'}`}
                        />
                    </div>

                    <ViewerButton label='Close' onClick={() => setViewerOpen(false)} className='absolute top-4 right-4'>
                        <IconX size={22} aria-hidden='true' />
                    </ViewerButton>

                    {count > 1 && <>
                        <ViewerButton label='Previous photo' onClick={showPrevious} className='absolute left-4 top-1/2 -translate-y-1/2'>
                            <IconChevronLeft size={24} aria-hidden='true' />
                        </ViewerButton>
                        <ViewerButton label='Next photo' onClick={showNext} className='absolute right-4 top-1/2 -translate-y-1/2'>
                            <IconChevronRight size={24} aria-hidden='true' />
                        </ViewerButton>
                        <div className='absolute bottom-5 left-1/2 -translate-x-1/2 text-zinc-300 text-sm' aria-hidden='true'>
                            {index + 1} / {count}
                        </div>
                    </>}
                </div>
            </Modal>
        </div>
    );
};

export default ItemGallery
